import React, { useEffect, useState } from 'react';
import { listenAssignments, fetchSchoolCalendar } from './api';
import { weekNumberForDate, todayIso } from './calendarMath';
import { coverageWeeks, unplacedForWeek } from './reporting';

const WEEKS_BEFORE = 1;
const WEEKS_AFTER = 3;

// "Quickly see any days with no or insufficient assignments" (gap-review
// item 4) — a day is flagged if nothing is scheduled at all, or nothing on
// it counts toward the must-do floor (isFloor), reusing the same field the
// year-plan seed already sets rather than adding a new one. Windowed to
// current week ± a few, not the full 179-day year — that's what "quickly"
// means in practice.
export default function CoverageTab({ familyData, student }) {
  const [assignments, setAssignments] = useState(null);
  const [calendar, setCalendar] = useState(undefined);

  useEffect(() => {
    if (!familyData?.id || !student?.studentEmail) return;
    const unsub = listenAssignments(familyData.id, student.studentEmail, setAssignments);
    fetchSchoolCalendar(familyData.id).then(setCalendar);
    return unsub;
  }, [familyData?.id, student?.studentEmail]);

  if (assignments === null || calendar === undefined) return <p className="settings-note">Loading…</p>;
  if (!calendar?.weeks?.length) return <p className="settings-note">No school calendar seeded yet.</p>;

  const currentWeek = weekNumberForDate(calendar.weeks, todayIso());
  const { weeks, flaggedCount } = coverageWeeks(assignments, calendar, {
    currentWeek, before: WEEKS_BEFORE, after: WEEKS_AFTER,
  });
  const unplaced = unplacedForWeek(assignments, currentWeek).length;
  const firstWeek = weeks[0]?.weekNumber ?? currentWeek;
  const lastWeek = weeks[weeks.length - 1]?.weekNumber ?? currentWeek;

  return (
    <div>
      <p className="settings-note" style={{ marginTop: 12 }}>
        {flaggedCount === 0
          ? `No coverage gaps in weeks ${firstWeek}–${lastWeek}.`
          : `${flaggedCount} day(s) with no work or no must-do items, weeks ${firstWeek}–${lastWeek}.`}
      </p>
      {unplaced > 0 && (
        <p className="settings-note">
          {unplaced} session{unplaced === 1 ? '' : 's'} this week still {unplaced === 1 ? 'has' : 'have'} no day —
          those aren't counted against any day above.
        </p>
      )}
      {weeks.map(w => (
        <div key={w.weekNumber} style={{ marginBottom: 12 }}>
          <div className="form-label" style={{ marginTop: 0 }}>
            Week {w.weekNumber}{w.weekNumber === currentWeek ? ' · This week' : ''}
          </div>
          <div className="edit-list">
            {w.days.map(d => (
              <div key={d.date} className="edit-list-row">
                <div className="edit-list-row__info">
                  <span className="edit-list-row__id-badge">{d.flagged ? '⚠️' : '✓'}</span>
                  <div>
                    <div className="edit-list-row__name">{d.date}{d.date === todayIso() ? ' · Today' : ''}</div>
                    <div className="edit-list-row__sub">
                      {d.count === 0
                        ? 'Nothing scheduled'
                        : `${d.count} item(s) · ${d.floorCount} must-do · ${d.totalMinutes} min`}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
